import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import {
  Action,
  Badge,
  Card,
  DateInput,
  Modal,
  NumberInput,
  SimpleSelect,
  TextInput,
  useToast,
} from 'corso-admin-ui';
import { type CommandAdminApi, commandStoreApi } from 'corso-types';
import { type FormEventHandler, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import api from '~/api';
import {
  DataTable,
  DataTableStatus,
  sharedTableDefinitions,
} from '~/components/dataTable';
import {
  useRefetchStore,
  useStoreContext,
  useStoreId,
} from '~/providers/StoreProvider';
import { formatter } from 'corso-web-lib';

type Store = CommandAdminApi.ResponseBody<'/store/:storeId', 'get'>;

type InvoiceDefinition = Store['invoiceDefinitions'][number];

const invoiceDefinitionCreateSchema =
  commandStoreApi['/store/:storeId/invoice-definition'].post.request.body;

const invoiceDefinitionUpdateSchema =
  commandStoreApi['/store/:storeId/invoice-definition/:invoiceDefinitionId'].put
    .request.body;

const frequencyOptions = [
  { value: 'Monthly', label: 'Monthly (Subscription)' },
  { value: 'Weekly', label: 'Weekly (Transactional Checkout Plus)' },
] satisfies { value: InvoiceDefinition['frequency']; label: string }[];

const chargeAmount = (charge: InvoiceDefinition['crewCharge']) =>
  charge ? formatter.currency(charge.amount, 'USD') : '-';

function AddInvoiceDefinitionModal({ onClose }: { onClose: () => void }) {
  const storeId = useStoreId();
  const refetch = useRefetchStore();
  const toast = useToast();

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(invoiceDefinitionCreateSchema),
    defaultValues: {
      name: '',
      frequency: 'Weekly' as const,
      startDate: '',
      termInMonths: 12,
      amount: 0,
      minimumAmount: 0,
      gspDiscount: 0,
    },
  });

  const { mutate: createInvoiceDefinition, isPending } = useMutation({
    mutationFn: api.store(storeId).createInvoiceDefinition,
    onSuccess: () => {
      onClose();
      refetch();
      toast.show('Created invoice definition');
    },
    onError: () => toast.show('Failed to create invoice definition'),
  });

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit((body) => createInvoiceDefinition(body))(event).catch(
      console.error,
    );
  };

  return (
    <Modal show onClose={onClose} title="Add Invoice Definition">
      <form className="flex flex-col gap-6" onSubmit={submitHandler}>
        <TextInput
          id="name"
          label="Name"
          required
          {...register('name')}
          error={errors.name?.message}
        />
        <Controller
          control={control}
          name="frequency"
          render={({ field, fieldState }) => (
            <SimpleSelect
              label="Frequency"
              required
              options={frequencyOptions}
              value={field.value}
              onChange={field.onChange}
              error={fieldState.error?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="startDate"
          render={({ field, fieldState }) => (
            <DateInput
              id="startDate"
              label="Start Date"
              required
              value={field.value || null}
              onChange={field.onChange}
              onBlur={field.onBlur}
              error={fieldState.error?.message}
            />
          )}
        />
        <NumberInput
          id="termInMonths"
          label="Term (months)"
          required
          {...register('termInMonths', { valueAsNumber: true })}
          error={errors.termInMonths?.message}
        />
        <NumberInput
          id="amount"
          label="Amount"
          required
          step="any"
          addon={{ insideStart: '$' }}
          {...register('amount', { valueAsNumber: true })}
          error={errors.amount?.message}
        />
        <NumberInput
          id="minimumAmount"
          label="Minimum Amount"
          required
          step="any"
          addon={{ insideStart: '$' }}
          {...register('minimumAmount', { valueAsNumber: true })}
          error={errors.minimumAmount?.message}
        />
        <NumberInput
          id="gspDiscount"
          label="GSP Discount"
          required
          step="any"
          addon={{ insideStart: '$' }}
          {...register('gspDiscount', { valueAsNumber: true })}
          error={errors.gspDiscount?.message}
        />
        <div className="flex justify-end">
          <Action variant="primary" type="submit" loading={isPending}>
            Add
          </Action>
        </div>
      </form>
    </Modal>
  );
}

function EditInvoiceDefinitionModal({
  invoiceDefinition,
  onClose,
}: {
  invoiceDefinition: InvoiceDefinition;
  onClose: () => void;
}) {
  const storeId = useStoreId();
  const refetch = useRefetchStore();
  const toast = useToast();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(invoiceDefinitionUpdateSchema),
    defaultValues: { extensionInDays: 0 },
  });

  const { mutate: updateInvoiceDefinition, isPending } = useMutation({
    mutationFn: (
      body: CommandAdminApi.RequestBody<
        '/store/:storeId/invoice-definition/:invoiceDefinitionId',
        'put'
      >,
    ) => api.store(storeId).updateInvoiceDefinition(invoiceDefinition.id, body),
    onSuccess: () => {
      onClose();
      refetch();
      toast.show('Updated invoice definition');
    },
    onError: () => toast.show('Failed to update invoice definition'),
  });

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit((body) => updateInvoiceDefinition(body))(event).catch(
      console.error,
    );
  };

  return (
    <Modal show onClose={onClose} title="Update Invoice Definition">
      <form className="flex flex-col gap-6" onSubmit={submitHandler}>
        <NumberInput
          id="extensionInDays"
          label="Extension (days)"
          details="Number of days to extend the end date by."
          required
          {...register('extensionInDays', { valueAsNumber: true })}
          error={errors.extensionInDays?.message}
        />
        <div className="flex justify-end">
          <Action variant="primary" type="submit" loading={isPending}>
            Save
          </Action>
        </div>
      </form>
    </Modal>
  );
}

const { idColumn, createdOnColumn } = sharedTableDefinitions;

const handleDate = (value: string | null) =>
  value ? formatter.date(value) : '—';

export function InvoiceDefinitionsCard() {
  const { invoiceDefinitions } = useStoreContext();
  const [showAdd, setShowAdd] = useState(false);
  const [definitionToEdit, setDefinitionToEdit] =
    useState<InvoiceDefinition | null>(null);

  const columns: ColumnDef<InvoiceDefinition>[] = [
    idColumn<InvoiceDefinition>({ meta: { align: 'left' } }),
    {
      accessorKey: 'isActive',
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant={row.original.isActive ? 'success' : 'default'}>
          {row.original.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    { accessorKey: 'frequency', header: 'Frequency', enableSorting: true },
    {
      accessorKey: 'startDate',
      header: 'Start',
      enableSorting: true,
      cell: ({ row }) => handleDate(row.original.startDate),
    },
    {
      accessorKey: 'endDate',
      header: 'End',
      cell: ({ row }) => handleDate(row.original.endDate),
    },
    {
      id: 'crewCharge',
      header: 'Amount',
      meta: { align: 'right' },
      cell: ({ row }) => chargeAmount(row.original.crewCharge),
    },
    {
      id: 'crewChargeMinimum',
      header: 'Minimum Amount',
      meta: { align: 'right' },
      cell: ({ row }) => chargeAmount(row.original.crewChargeMinimum),
    },
    {
      id: 'gspCharge',
      header: 'GSP Credit',
      meta: { align: 'right' },
      cell: ({ row }) => chargeAmount(row.original.gspCharge),
    },
    {
      accessorKey: 'isTrial',
      header: 'Trial',
      cell: ({ row }) => (row.original.isTrial ? 'Yes' : 'No'),
    },
    createdOnColumn<InvoiceDefinition>(),
    {
      id: 'actions',
      header: '',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <Action
          variant="ghost"
          icon="edit"
          accessibilityLabel="Extend Invoice Definition"
          onClick={() => setDefinitionToEdit(row.original)}
        />
      ),
    },
  ];

  return (
    <Card>
      <Card.Line>
        <Card.Heading>Invoice Definitions</Card.Heading>
        <Action
          variant="ghost"
          icon="add"
          accessibilityLabel="Add Invoice Definition"
          onClick={() => setShowAdd(true)}
        />
      </Card.Line>

      {invoiceDefinitions.length > 0 ? (
        <DataTable
          data={invoiceDefinitions}
          columns={columns}
          pageSize={10}
          defaultSort={[
            { id: 'isActive', desc: true },
            { id: 'createdOn', desc: true },
          ]}
        />
      ) : (
        <DataTableStatus
          icon="receipt"
          title="No invoice definitions"
          details="Add an invoice definition to configure billing for this store."
        />
      )}

      {showAdd && (
        <AddInvoiceDefinitionModal onClose={() => setShowAdd(false)} />
      )}

      {definitionToEdit && (
        <EditInvoiceDefinitionModal
          invoiceDefinition={definitionToEdit}
          onClose={() => setDefinitionToEdit(null)}
        />
      )}
    </Card>
  );
}
